import { supabaseClient } from './index';
import { logger } from '../logger';
import type { Config } from '../../types/config';
import type { SupportedLanguages } from '../../i18n';

// Fonction pour obtenir l'ID de l'hôtel
export function getHotelId(): string {
  // Lire depuis window.HOTEL_CONFIG
  if (window.HOTEL_CONFIG?.id) {
    return window.HOTEL_CONFIG.id;
  }
  
  // Fallback à l'ID par défaut (à utiliser uniquement en développement)
  return '650e1059-753d-4ac2-a961-e7f1eb298e14';
}

// ID de l'hôtel obtenu dynamiquement
export const HOTEL_ID = getHotelId();

class ConfigClient {
  private static instance: ConfigClient;
  private configCache: Config | null = null;
  private loadPromise: Promise<Config> | null = null;
  private retryCount: number = 0;
  private maxRetries: number = 5;

  private constructor() {}

  public static getInstance(): ConfigClient {
    if (!ConfigClient.instance) {
      ConfigClient.instance = new ConfigClient();
    }
    return ConfigClient.instance;
  }

  private getGmapId(style: string): string {
    // Convertir le style en ID Google Maps
    switch (style) {
      case 'color':
        return 'cbb0ea856f9bc8cd';
      case 'noir_et_blanc':
        return '1617805941c4af2d';
      default:
        return '1617805941c4af2d'; // Fallback sur noir et blanc
    }
  }

  public async getConfig(): Promise<Config> {
    // Si on a déjà le config en cache, le retourner
    if (this.configCache) {
      logger.debug('[CONFIG_CACHE_HIT] Using cached hotel config', {
        category: 'API',
        data: { 
          hotelId: HOTEL_ID,
          timestamp: new Date().toISOString()
        }
      });
      return this.configCache;
    }

    // Si un chargement est déjà en cours, retourner la promesse existante
    if (this.loadPromise) {
      logger.debug('[CONFIG_LOADING] Reusing existing config loading promise', {
        category: 'API',
        data: { 
          hotelId: HOTEL_ID,
          timestamp: new Date().toISOString()
        }
      });
      return this.loadPromise;
    }

    // Sinon, charger la configuration
    this.loadPromise = this.fetchConfig();
    
    try {
      const config = await this.loadPromise;
      this.configCache = config;
      this.loadPromise = null;
      this.retryCount = 0;
      return config;
    } catch (error) {
      this.loadPromise = null;
      
      // Retry logic
      if (this.retryCount < this.maxRetries) {
        this.retryCount++;
        logger.warn(`[CONFIG_RETRY] Retrying config fetch (${this.retryCount}/${this.maxRetries})`, {
          category: 'API',
          data: { 
            hotelId: HOTEL_ID,
            error,
            timestamp: new Date().toISOString()
          }
        });
        
        // Exponential backoff
        const delay = Math.min(1000 * Math.pow(2, this.retryCount - 1), 30000);
        await new Promise(resolve => setTimeout(resolve, delay));
        
        return this.getConfig();
      }
      
      throw error;
    }
  }

  private async fetchConfig(): Promise<Config> {
    try {
      logger.debug('[CONFIG_FETCH] Loading hotel config from Supabase', {
        category: 'API',
        data: { 
          hotelId: HOTEL_ID,
          timestamp: new Date().toISOString()
        }
      });

      const { data, error } = await supabaseClient
        .from('hotels')
        .select('*, logo_metadata, image_metadata, image_infobulle_metadata')
        .eq('id', HOTEL_ID)
        .single();

      if (error) {
        logger.error('[CONFIG_ERROR] Error fetching hotel config', {
          category: 'API',
          data: { 
            error,
            hotelId: HOTEL_ID,
            timestamp: new Date().toISOString()
          }
        });
        throw error;
      }

      if (!data) {
        logger.error('[CONFIG_NOT_FOUND] No hotel configuration found', {
          category: 'API',
          data: { 
            hotelId: HOTEL_ID,
            timestamp: new Date().toISOString()
          }
        });
        throw new Error(`No hotel configuration found for ID: ${HOTEL_ID}`);
      }

      // Log raw data for debugging
      logger.debug('[CONFIG_RAW_DATA] Raw hotel data from Supabase', {
        category: 'API',
        data: {
          id: data.id,
          name: data.name,
          available_languages: data.available_languages,
          open_with_stay: data.open_with_stay,
          open_with_stay_type: typeof data.open_with_stay,
          timestamp: new Date().toISOString()
        }
      });

      // Get image URLs from metadata
      const logo = data.logo_metadata?.url ? [{ url: data.logo_metadata.url }] : undefined;
      const image = data.image_metadata?.url;
      const imageInfobulle = data.image_infobulle_metadata?.url;

      // Ajouter "px" au border_radius
      const borderRadius = data.border_radius ? `${data.border_radius}px` : '0.5rem';

      // Convertir le style de carte en ID Google Maps
      const gmapId = this.getGmapId(data.id_gmap);

      // Parse available_languages from JSON string if needed
      let availableLanguages;
      try {
        if (typeof data.available_languages === 'string') {
          availableLanguages = JSON.parse(data.available_languages);
        } else if (Array.isArray(data.available_languages)) {
          availableLanguages = data.available_languages;
        } else {
          availableLanguages = ['fr', 'en'];
        }
      } catch (e) {
        logger.error('[CONFIG_LANGUAGE_ERROR] Error parsing available languages', {
          category: 'API',
          data: { 
            error: e,
            value: data.available_languages,
            timestamp: new Date().toISOString()
          }
        });
        availableLanguages = ['fr', 'en'];
      }

      logger.debug('[CONFIG_LANGUAGES] Processed available languages', {
        category: 'API',
        data: {
          raw: data.available_languages,
          processed: availableLanguages,
          isArray: Array.isArray(availableLanguages),
          type: typeof availableLanguages,
          timestamp: new Date().toISOString()
        }
      });

      const config = {
        id: data.id,
        name: data.name,
        url: data.url || '',
        telephone: data.telephone,
        latitude: data.latitude,
        longitude: data.longitude,
        'dark-color': data.dark_color,
        'medium-color': data.medium_color,
        'light-color': data.light_color,
        'major-color': data.major_color,
        'HFBack-color': data.hf_back_color,
        'HFTxt-color': data.hf_txt_color,
        'sectionBack-color': data.section_back_color,
        'homeBack-color': data.home_back_color,
        border_radius: borderRadius,
        colors_map: data.colors_map,
        font_family: data.font_family || 'system-ui',
        font_url: data.font_url,
        fulladress: data.fulladress,
        timezone: data.timezone,
        defaultLanguage: data.defaultLanguage,
        available_languages: availableLanguages,
        logo,
        image,
        image_infobulle: imageInfobulle,
        id_Gmap: gmapId,
        nav_txt: data.nav_txt_color,
        nav_icon: data.nav_icon_color,
        nav_back: data.nav_back_color,
        count_back: data.count_back_color,
        count_txt: data.count_txt_color,
        booking: data.booking,
        limit_walk: data.limit_walk?.toString() || '30',
        open_with_stay: data.open_with_stay
      };

      logger.debug('[CONFIG_PROCESSED] Hotel config processed successfully', {
        category: 'API',
        data: {
          hotelId: HOTEL_ID,
          name: config.name,
          availableLanguages: config.available_languages,
          open_with_stay: config.open_with_stay,
          open_with_stay_type: typeof config.open_with_stay,
          timestamp: new Date().toISOString()
        }
      });

      return config;

    } catch (error) {
      logger.error('[CONFIG_FETCH_ERROR] Error loading hotel config', {
        category: 'API',
        data: { 
          error,
          errorDetails: error instanceof Error ? {
            message: error.message,
            stack: error.stack
          } : error,
          hotelId: HOTEL_ID,
          timestamp: new Date().toISOString()
        }
      });
      throw error;
    }
  }

  public clearCache(): void {
    this.configCache = null;
    this.loadPromise = null;
    this.retryCount = 0;
    logger.debug('[CONFIG_CACHE_CLEAR] Config cache cleared', {
      category: 'API',
      data: { 
        timestamp: new Date().toISOString()
      }
    });
  }
}

export const configClient = ConfigClient.getInstance();